import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type NavThemeMode = 'dark' | 'light';

interface NavThemeContextType {
  mode: NavThemeMode;
  toggleMode: () => void;
  tokens: NavThemeTokens;
}

const NavThemeContext = createContext<NavThemeContextType | undefined>(undefined);

// ==============================|| NAV THEME TOKENS ||============================== //

export interface NavThemeTokens {
  // Backgrounds
  bgPrimary: string;
  bgSecondary: string;
  bgTertiary: string;
  bgCard: string;
  bgCardHover: string;
  bgSelected: string;
  bgSelectedHover: string;
  bgOverlay: string;

  // Text
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  textDimmed: string;

  // Icons
  iconDefault: string;
  iconHover: string;
  iconActive: string;

  // Accent colors
  accentPrimary: string;
  accentSecondary: string;
  accentTertiary: string;
  accentGlow: string;

  // Borders
  borderSubtle: string;
  borderMedium: string;
  borderStrong: string;
  borderAccent: string;

  // Shadows
  shadowSoft: string;
  shadowMedium: string;
  shadowGlow: string;

  // Gradients
  gradientPrimary: string;
  gradientHover: string;
  gradientSelected: string;
  gradientAccentBar: string;
  gradientDivider: string;
}

export const navThemeTokens: Record<NavThemeMode, NavThemeTokens> = {
  dark: {
    // Backgrounds
    bgPrimary: '#08080c',
    bgSecondary: '#0a0a10',
    bgTertiary: '#0c0c14',
    bgCard: 'rgba(99, 102, 241, 0.04)',
    bgCardHover: 'rgba(99, 102, 241, 0.08)',
    bgSelected: 'linear-gradient(135deg, rgba(99, 102, 241, 0.22) 0%, rgba(129, 140, 248, 0.12) 100%)',
    bgSelectedHover: 'linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(129, 140, 248, 0.15) 100%)',
    bgOverlay: 'rgba(8, 8, 12, 0.98)',

    // Text
    textPrimary: '#fafafa',
    textSecondary: 'rgba(255, 255, 255, 0.9)',
    textMuted: 'rgba(255, 255, 255, 0.6)',
    textDimmed: 'rgba(255, 255, 255, 0.55)',

    // Icons
    iconDefault: 'rgba(255, 255, 255, 0.5)',
    iconHover: 'rgba(255, 255, 255, 0.7)',
    iconActive: '#818cf8',

    // Accent colors
    accentPrimary: '#6366f1',
    accentSecondary: '#818cf8',
    accentTertiary: '#a5b4fc',
    accentGlow: 'rgba(99, 102, 241, 0.3)',

    // Borders
    borderSubtle: 'rgba(99, 102, 241, 0.1)',
    borderMedium: 'rgba(99, 102, 241, 0.15)',
    borderStrong: 'rgba(99, 102, 241, 0.2)',
    borderAccent: 'rgba(99, 102, 241, 0.3)',

    // Shadows
    shadowSoft: '0 4px 16px rgba(0, 0, 0, 0.3)',
    shadowMedium: '0 8px 32px rgba(0, 0, 0, 0.4)',
    shadowGlow: '0 4px 20px rgba(99, 102, 241, 0.2)',

    // Gradients
    gradientPrimary: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
    gradientHover: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
    gradientSelected: 'linear-gradient(135deg, rgba(99, 102, 241, 0.22) 0%, rgba(129, 140, 248, 0.12) 100%)',
    gradientAccentBar: 'linear-gradient(180deg, #a5b4fc 0%, #6366f1 100%)',
    gradientDivider: 'linear-gradient(90deg, transparent 0%, rgba(99, 102, 241, 0.2) 50%, transparent 100%)',
  },
  light: {
    // Backgrounds - clean, warm whites
    bgPrimary: '#ffffff',
    bgSecondary: '#f8f9fc',
    bgTertiary: '#f1f3f8',
    bgCard: 'rgba(99, 102, 241, 0.04)',
    bgCardHover: 'rgba(99, 102, 241, 0.08)',
    bgSelected: 'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(129, 140, 248, 0.06) 100%)',
    bgSelectedHover: 'linear-gradient(135deg, rgba(99, 102, 241, 0.16) 0%, rgba(129, 140, 248, 0.1) 100%)',
    bgOverlay: 'rgba(255, 255, 255, 0.98)',

    // Text - strong contrast
    textPrimary: '#0f172a',
    textSecondary: '#1e293b',
    textMuted: '#475569',
    textDimmed: '#64748b',

    // Icons
    iconDefault: '#64748b',
    iconHover: '#475569',
    iconActive: '#4f46e5',

    // Accent colors - deeper for light mode
    accentPrimary: '#4f46e5',
    accentSecondary: '#6366f1',
    accentTertiary: '#4338ca',
    accentGlow: 'rgba(79, 70, 229, 0.2)',

    // Borders - subtle but visible
    borderSubtle: 'rgba(99, 102, 241, 0.1)',
    borderMedium: 'rgba(99, 102, 241, 0.15)',
    borderStrong: 'rgba(99, 102, 241, 0.2)',
    borderAccent: 'rgba(99, 102, 241, 0.25)',

    // Shadows - soft and natural
    shadowSoft: '0 2px 12px rgba(15, 23, 42, 0.06)',
    shadowMedium: '0 4px 24px rgba(15, 23, 42, 0.1)',
    shadowGlow: '0 4px 20px rgba(99, 102, 241, 0.15)',

    // Gradients
    gradientPrimary: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
    gradientHover: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)',
    gradientSelected: 'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(129, 140, 248, 0.06) 100%)',
    gradientAccentBar: 'linear-gradient(180deg, #6366f1 0%, #4338ca 100%)',
    gradientDivider: 'linear-gradient(90deg, transparent 0%, rgba(99, 102, 241, 0.15) 50%, transparent 100%)',
  },
};

// ==============================|| NAV THEME PROVIDER ||============================== //

// Cookie helpers — synlets-theme cookie is shared across subdomains (.synlets.com)
const THEME_COOKIE = 'synlets-theme';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

function getThemeCookie(): NavThemeMode | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${THEME_COOKIE}=(dark|light)`));
  return match ? (match[1] as NavThemeMode) : null;
}

function setThemeCookie(mode: NavThemeMode) {
  // On *.synlets.com the cookie is shared across subdomains; on localhost it's per-origin
  const isProduction = window.location.hostname.endsWith('synlets.com');
  const domain = isProduction ? '; domain=.synlets.com' : '';
  document.cookie = `${THEME_COOKIE}=${mode}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${domain}`;
}

export function NavThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<NavThemeMode>('dark');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    // Priority 1: Cookie (shared across subdomains in production)
    const cookieMode = getThemeCookie();
    if (cookieMode) {
      setMode(cookieMode);
      return;
    }

    // Priority 2: localStorage fallback (same-origin only)
    const savedMode = localStorage.getItem('synlets-theme') as NavThemeMode;
    if (savedMode && (savedMode === 'dark' || savedMode === 'light')) {
      setMode(savedMode);
      return;
    }

    // Priority 3: System preference
    if (typeof window !== 'undefined' && window.matchMedia) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setMode(prefersDark ? 'dark' : 'light');
    }
  }, []);

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      // Only auto-switch if user hasn't explicitly set a preference
      if (!getThemeCookie() && !localStorage.getItem('synlets-theme')) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (mounted) {
      // Write to both cookie (cross-subdomain) and localStorage (fast local reads)
      setThemeCookie(mode);
      localStorage.setItem('synlets-theme', mode);
      // Data attributes for CSS selectors
      document.documentElement.setAttribute('data-nav-theme', mode);
      document.documentElement.setAttribute('data-color-scheme', mode);
    }
  }, [mode, mounted]);

  const toggleMode = () => {
    setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const tokens = navThemeTokens[mode];

  return (
    <NavThemeContext.Provider value={{ mode, toggleMode, tokens }}>
      {children}
    </NavThemeContext.Provider>
  );
}

// ==============================|| USE NAV THEME HOOK ||============================== //

export function useNavTheme() {
  const context = useContext(NavThemeContext);
  if (context === undefined) {
    throw new Error('useNavTheme must be used within a NavThemeProvider');
  }
  return context;
}

export default NavThemeContext;
